/**
 * @fileoverview Production console interception and error forwarding.
 *
 * In a production build the browser console is the only place a client-side
 * failure is recorded, and nobody is watching it. This module silences the
 * chatty console methods and re-points `console.warn` / `console.error` — plus
 * the two global failure events — at the `logWarning` / `logError` callables,
 * so breakage on a deployed client reaches Cloud Logging.
 *
 * Installed by {@link bootstrapCipApp} rather than written out in each
 * entrypoint, so the two cannot drift apart again — the drift that left
 * `pai.stanford.edu` running as a production site with no telemetry at all
 * ([#3696](https://github.com/CodeinPlace/cip/issues/3696)).
 */

import {getFunctions, httpsCallable} from 'firebase/functions';
import {
  getOrCreateSessionId,
  createBrowserExtensionErrorGate,
} from '@cip/core/utils';
import {BUILD_COMMIT, IS_PRODUCTION} from 'utils/general';
import {isFirestoreDebugLoggingRequested} from './firestoreDebugLogging';

/**
 * Identifies which deployed client produced a log entry. The callables store
 * it as `web_app` (see `frontend-logging.ts`), which is the only thing
 * separating one app's entries from another's once they are in the same log —
 * so every entrypoint must pass a distinct value.
 */
export type CipWebAppId = 'cip-webapp' | 'cip-pai';

/**
 * Known-benign console warnings emitted by third-party libraries on every
 * mount. In production every console.warn becomes a logWarning cloud function
 * call, so a per-render library deprecation notice floods the function with
 * one invocation per page view. Drop these before they reach logWarning.
 *
 * Shared across every app because they come from the shared `<App>` shell, not
 * from any one deploy wrapper.
 *
 * TODO: remove the relevant entry once the underlying library is upgraded or
 * replaced (e.g. migrating off react-pro-sidebar's ProSidebarProvider).
 */
const SUPPRESSED_WARNING_SUBSTRINGS = ['ProSidebarProvider is deprecated'];

/**
 * Decides whether a `console.warn` call is one of the known-benign
 * per-render library warnings that must not reach the logWarning callable.
 *
 * @param args - The arguments the caller passed to `console.warn`.
 * @returns True when any string argument contains a suppressed substring.
 * @throws Never.
 */
export function isSuppressedWarning(args: unknown[]): boolean {
  return args.some(
    arg =>
      typeof arg === 'string' &&
      SUPPRESSED_WARNING_SUBSTRINGS.some(substring => arg.includes(substring)),
  );
}

/**
 * Flattens console arguments into something the callable can serialize.
 *
 * Errors and Route `ErrorResponse` objects keep their useful fields on the
 * prototype or as non-enumerable properties, so a plain structured clone drops
 * exactly the parts worth reading. Copy those across explicitly.
 *
 * @param args - The arguments the caller passed to `console.warn`/`.error`.
 * @returns The same values with `message`, `stack`, and `name` lifted onto any
 *   object entries, recursing through arrays.
 * @throws Never.
 */
export function serializeForLog(args: unknown[]): unknown[] {
  return args.map(item => {
    if (Array.isArray(item)) {
      return serializeForLog(item);
    }
    if (item && typeof item === 'object') {
      return {
        ...item,
        ...('message' in item ? {message: item.message} : {}),
        ...('stack' in item ? {stack: item.stack} : {}),
        ...('name' in item ? {name: item.name} : {}),
      };
    }
    return item;
  });
}

/**
 * Silences the chatty console methods and forwards warnings, errors, uncaught
 * exceptions, and unhandled rejections to the logging callables.
 *
 * No-op outside a production build, and opted out entirely by
 * `?firestoreDebug=1`: production builds silence console.log and console.debug,
 * which is exactly where the Firestore SDK writes its debug stream, so leaving
 * this in place would make the flag do nothing on a hosted build — the only
 * place the stall reproduces reliably. Skipping the warn/error interception too
 * keeps a debug run (thousands of lines) from being forwarded to the logWarning
 * function. See [#3697](https://github.com/CodeinPlace/cip/issues/3697).
 *
 * @param params.webApp - Which client is reporting; stored as `web_app` on
 *   every entry. See {@link CipWebAppId}.
 * @returns Nothing.
 * @throws Never — a callable that rejects is dropped rather than reported, see
 *   the note on the reporting-failure handler below.
 */
export function installProductionLogging({
  webApp,
}: {
  webApp: CipWebAppId;
}): void {
  if (!IS_PRODUCTION || isFirestoreDebugLoggingRequested()) {
    return;
  }

  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
  console.trace = () => {};
  console.table = () => {};

  const functions = getFunctions();
  const logError = httpsCallable(functions, 'logError');
  const logWarning = httpsCallable(functions, 'logWarning');

  // Reporting runs over the same transport it reports on, so a report that
  // fails must not be reportable. Without this, a rejected callable becomes an
  // unhandled rejection, the `unhandledrejection` listener below reports it,
  // that report fails the same way, and the client spins — one Safari session
  // emitted 100 identical `FirebaseError: internal` entries, each of them a
  // cloud function invocation, on a connection that was already struggling.
  // There is nowhere left to report a broken reporter, so drop it.
  const dropReportingFailure = () => {};

  // Extensions inject scripts into the page, so their throws reach our window
  // error handler and bill us one logError invocation each. Report the first
  // throw per extension per session and drop the rest. Refs #3127.
  const extensionErrors = createBrowserExtensionErrorGate();

  /** Metadata every entry carries, resolved at report time. */
  const reportContext = () => ({
    url: window.location.href,
    userAgent: navigator.userAgent,
    webApp,
    sessionId: getOrCreateSessionId(),
    clientCommit: BUILD_COMMIT,
  });

  console.warn = (...logged) => {
    if (isSuppressedWarning(logged)) {
      return;
    }
    logWarning({
      logged: serializeForLog(logged),
      ...reportContext(),
    }).catch(dropReportingFailure);
  };

  console.error = (...logged) => {
    const serialized = serializeForLog(logged);
    if (extensionErrors.shouldDropLoggedArgs(serialized)) {
      return;
    }
    logError({
      logged: serialized,
      ...reportContext(),
    }).catch(dropReportingFailure);
  };

  const reportError = (error: unknown, filename?: string) => {
    const isError = error instanceof Error;
    const stack = isError ? error.stack : undefined;
    if (extensionErrors.shouldDrop({stack, filename})) {
      return;
    }
    logError({
      logged: {
        message: isError ? error.message : String(error),
        stack,
        name: isError ? error.name : undefined,
      },
      ...reportContext(),
    }).catch(dropReportingFailure);
  };

  window.addEventListener('error', event => {
    // `event.filename` is the only attribution available for a cross-origin
    // script, where the browser withholds `event.error` and its stack.
    reportError(event.error ?? event.message, event.filename);
  });

  window.addEventListener('unhandledrejection', event => {
    reportError(event.reason ?? 'unhandledrejection');
  });
}
